import { CheckoutSection } from 'server/types/pmts-overrides';
import { WizardState, TenantCourse } from './campaign-wizard';
import { rpx } from 'client/lib/rpx-client';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  IcoChevronDown,
  IcoChevronUp,
  IcoTrash,
  IcoPlus,
  IcoRocketLaunch,
  IcoCreditCard,
  IcoGift,
  IcoCalendar,
  IcoTag,
  IcoSparkles,
  IcoEye,
} from '@components/icons';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { SectionEditor, SECTION_TYPE_META } from './section-editor';
import { OntraportTagPicker } from './ontraport-tag-picker';
import { showError } from '@components/app-error';
import { CampaignPageShell } from '@course/components/campaign/campaign-page-shell';
import { SectionRenderer } from '@course/components/campaign/section-renderer';
import { CourseSelect } from './course-select';
import { Currency } from 'server/types';
import * as pmtfmt from 'shared/payments/fmt';

type Props = {
  state: WizardState;
  courses: TenantCourse[];
  updateState: (partial: Partial<WizardState>) => void;
};

const SECTION_TYPE_LABELS: Record<string, string> = {
  hero: 'Hero',
  eyebrow: 'Eyebrow Text',
  content: 'Content',
  callout: 'Callout',
  valueStack: 'Benefits / Value Stack',
  video: 'Video',
  trust: 'Trust Badges',
  testimonials: 'Testimonials',
  faq: 'FAQ',
  cta: 'Call to Action',
  footer: 'Footer',
};

const UPSELL_SECTION_TYPES: CheckoutSection['type'][] = [
  'hero',
  'eyebrow',
  'content',
  'callout',
  'valueStack',
  'faq',
  'testimonials',
  'video',
  'trust',
  'cta',
  'footer',
];

function createDefaultSection(type: CheckoutSection['type']): CheckoutSection {
  switch (type) {
    case 'hero':
      return {
        type: 'hero',
        settings: { gradientFrom: '#0089bd', gradientTo: '#013ea6' },
        content: { headline: '' },
      };
    case 'eyebrow':
      return { type: 'eyebrow', content: { text: '' } };
    case 'content':
      return {
        type: 'content',
        settings: { maxWidth: 'wide' },
        content: { blocks: [{ type: 'paragraph', text: '' }] },
      };
    case 'callout':
      return { type: 'callout', content: { icon: 'book', heading: '' } };
    case 'valueStack':
      return { type: 'valueStack', content: { items: [{ bold: '', text: '' }] } };
    case 'faq':
      return { type: 'faq', content: { heading: 'Frequently Asked Questions', items: [] } };
    case 'testimonials':
      return { type: 'testimonials', content: { items: [] } };
    case 'video':
      return { type: 'video', content: { url: '' } };
    case 'trust':
      return { type: 'trust', content: { badges: ['secure-checkout', 'money-back'] } };
    case 'cta':
      return {
        type: 'cta',
        content: { acceptText: 'Yes, add this!', declineText: 'No thanks, continue' },
      };
    case 'footer':
      return { type: 'footer', content: {} };
    default:
      return { type: 'content', settings: { maxWidth: 'wide' }, content: { blocks: [] } };
  }
}

function formatPrice(price: UpsellPrice) {
  if (!price.priceInCents) {
    return 'Free';
  }
  // Explicit (US$ / CA$) because USD and CAD prices sit side by side here.
  const amount = pmtfmt.price({
    priceInCents: price.priceInCents,
    currency: price.currency ?? 'USD',
    explicitCurrency: true,
  });
  const suffix = price.paymentType === 'subscription' && price.interval ? `/${price.interval}` : '';
  return `${amount}${suffix}`;
}

type UpsellPrice = {
  id: string;
  name?: string;
  priceInCents?: number;
  currency?: Currency;
  paymentType: string;
  interval?: string;
  freeTrialPeriod?: number;
};

/**
 * The currencies buyers can check out in on this campaign: those of its default
 * and "Also offer" prices (every price when no default is set yet, matching
 * what the server attaches the upsell to), default currency first. Each one
 * needs its own upsell price, since a buyer is offered the upsell in the
 * currency they just paid in.
 */
export function campaignCurrencies(state: WizardState): Currency[] {
  const ids = new Set([state.selectedPriceId, ...state.alternativePriceIds].filter(Boolean));
  const offered = state.prices.filter((p) => ids.has(p.id));
  const prices = offered.length ? offered : state.prices;
  const isDefault = (p: { id: string }) => (p.id === state.selectedPriceId ? 0 : 1);
  const sorted = [...prices].sort((a, b) => isDefault(a) - isDefault(b));
  const currencies = [...new Set(sorted.map((p) => p.currency ?? 'USD'))];
  return currencies.length ? currencies : ['USD'];
}

type Audience = 'default' | 'returners';

function AIGeneratePanel({
  state,
  audience,
  onGenerated,
}: {
  state: WizardState;
  audience: Audience;
  onGenerated: (sections: CheckoutSection[]) => void;
}) {
  const [text, setText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  async function handleGenerate() {
    if (!text.trim() || !state.courseId) {
      return;
    }
    setGenerating(true);
    try {
      const preamble =
        audience === 'returners'
          ? `This is a POST-PURCHASE UPSELL page shown ONLY to buyers who have already used a free AI Playground trial. They know the product. There is no new free trial — they pay the standard monthly price immediately. Frame as continuation/commitment ("come back", "now's the time", "continue at the standard rate"), NOT discovery. The default accept-button text is "Join AI Playground" — override if the copy suggests something stronger.`
          : `This is a POST-PURCHASE UPSELL page. The customer just bought something and we want to offer them an additional product. Generate a compelling upsell page layout.`;
      const acceptDefault = audience === 'returners' ? 'Join AI Playground' : 'Yes, add this!';
      const result = await rpx.aiCheckoutBuilder.generateCheckoutPage({
        courseId: state.courseId,
        marketingText: `${preamble}\n\n${text.trim()}`,
        priceInfo: {
          name: 'Upsell',
          amount: 0,
          paymentType: 'subscription',
        },
      });
      if (result.config.sections.length > 0) {
        const sections = result.config.sections.map((s: any) => {
          if (s.type === 'checkoutForm') {
            return {
              type: 'cta' as const,
              content: { acceptText: acceptDefault, declineText: 'No thanks, continue' },
            };
          }
          return s;
        });
        const hasCta = sections.some((s: any) => s.type === 'cta');
        if (!hasCta) {
          sections.push({
            type: 'cta' as const,
            content: { acceptText: acceptDefault, declineText: 'No thanks, continue' },
          });
        }
        onGenerated(sections);
        setCollapsed(true);
      }
    } catch (err) {
      showError(err);
    } finally {
      setGenerating(false);
    }
  }

  if (collapsed) {
    return (
      <div class="mb-4 flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
        <span class="text-green-700 font-medium">
          Sections generated from your content. Edit below to customize.
        </span>
        <button
          type="button"
          class="text-green-600 underline text-sm"
          onClick={() => setCollapsed(false)}
        >
          Generate again
        </button>
      </div>
    );
  }

  return (
    <div class="mb-4 bg-indigo-50/50 rounded-xl p-5">
      <div class="flex items-center gap-2 mb-1">
        <IcoSparkles class="size-4 text-indigo-400" />
        <h4 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">AI Generate</h4>
      </div>
      <p class="text-sm text-gray-500 mb-3">
        Paste your upsell copy or product description. AI will create the page sections
        automatically.
      </p>
      <textarea
        class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-24 mb-3"
        placeholder="Paste upsell copy, product benefits, or description..."
        value={text}
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
      />
      <div class="flex items-center justify-between">
        <span class="text-xs text-gray-400">
          {text.length > 0 ? `${text.length.toLocaleString()} characters` : ''}
        </span>
        <BtnPrimary
          onClick={handleGenerate}
          isLoading={generating}
          disabled={!text.trim() || !state.courseId}
        >
          {generating ? 'Generating...' : 'Generate Sections'}
        </BtnPrimary>
      </div>
    </div>
  );
}

function PreviewModal({
  sections,
  brand,
  audience,
  onClose,
}: {
  sections: CheckoutSection[];
  brand?: string;
  audience: Audience;
  onClose(): void;
}) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const label =
    audience === 'returners'
      ? 'Upsell Preview (Previous trial users) — shown to buyers who already used an AIP free trial'
      : 'Upsell Preview (Default) — this is how the page will look after purchase';

  return (
    <div style="position: fixed; inset: 0; z-index: 9999; background: #fff; overflow-y: auto">
      <div style="position: sticky; top: 0; z-index: 10; background: rgba(0,0,0,0.8); display: flex; align-items: center; justify-content: space-between; padding: 8px 16px">
        <span style="color: #fff; font-size: 13px; font-weight: 500">{label}</span>
        <button
          type="button"
          onClick={onClose}
          style="background: #fff; color: #111; border: none; border-radius: 6px; padding: 6px 16px; font-size: 13px; font-weight: 600; cursor: pointer"
        >
          Close Preview
        </button>
      </div>
      <CampaignPageShell brand={brand}>
        <SectionRenderer sections={sections} brand={brand} />
      </CampaignPageShell>
    </div>
  );
}

function OfferSummary({
  state,
  selectedPrices,
}: {
  state: WizardState;
  selectedPrices: UpsellPrice[];
}) {
  if (!state.upsellProductCourseId || !selectedPrices.length) {
    return null;
  }

  const trialDays = state.upsellFreeTrialDays;
  const hasDeferred = !!state.upsellDeferredDate;

  function buildPriceLine(price: UpsellPrice) {
    if (price.paymentType === 'free' || !price.priceInCents) {
      return 'Free (no charge)';
    }
    const base = formatPrice(price);
    if (trialDays) {
      return `Nothing today, then ${base} after ${trialDays}-day free trial`;
    }
    return base;
  }

  function formatDate(dateStr: string) {
    const [y, m, d] = dateStr.split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  return (
    <div class="rounded-lg border border-indigo-200 bg-indigo-50 p-4 flex flex-col gap-2">
      <span class="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
        Offer Summary
      </span>
      <div class="flex flex-col gap-1 text-sm text-gray-700">
        <div class="flex gap-2">
          <span class="text-gray-500 w-24 flex-shrink-0">Product</span>
          <span class="font-medium">{state.upsellProductTitle}</span>
        </div>
        <div class="flex gap-2">
          <span class="text-gray-500 w-24 flex-shrink-0">Price</span>
          <span class="font-medium flex flex-col">
            {selectedPrices.map((price) => (
              <span key={price.id}>
                {selectedPrices.length > 1 && `${price.currency ?? 'USD'} buyers: `}
                {buildPriceLine(price)}
              </span>
            ))}
          </span>
        </div>
        <div class="flex gap-2">
          <span class="text-gray-500 w-24 flex-shrink-0">Enrollment</span>
          <span class="font-medium">
            {hasDeferred
              ? `Deferred to ${formatDate(state.upsellDeferredDate)}`
              : 'Immediate after purchase'}
          </span>
        </div>
        {state.upsellOntraportTags.length > 0 && (
          <div class="flex gap-2">
            <span class="text-gray-500 w-24 flex-shrink-0">Tags</span>
            <span class="font-medium">{state.upsellOntraportTags.join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function UpsellStep({ state, courses, updateState }: Props) {
  const [upsellPrices, setUpsellPrices] = useState<UpsellPrice[]>([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | undefined>(undefined);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState<Audience>('default');
  const addMenuRef = useRef<HTMLDivElement>(null);
  const fetchIdRef = useRef(0);

  const activeSections =
    activeTab === 'returners' ? state.upsellSectionsReturners : state.upsellSections;

  function setActiveSections(sections: CheckoutSection[]) {
    if (activeTab === 'returners') {
      updateState({ upsellSectionsReturners: sections });
    } else {
      updateState({ upsellSections: sections });
    }
  }

  useEffect(() => {
    if (state.upsellProductCourseId) {
      loadUpsellPrices(state.upsellProductCourseId);
    }
  }, [state.upsellProductCourseId]);

  useEffect(() => {
    if (!showAddMenu) {
      return;
    }
    function handleClickOutside(e: MouseEvent) {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
        setShowAddMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddMenu]);

  async function loadUpsellPrices(courseId: string) {
    const fetchId = ++fetchIdRef.current;
    setLoadingPrices(true);
    try {
      const prices = await rpx.campaigns.listCoursePrices({ courseId });
      if (fetchIdRef.current === fetchId) {
        setUpsellPrices(prices);
      }
    } catch {
      if (fetchIdRef.current === fetchId) {
        setUpsellPrices([]);
      }
    } finally {
      if (fetchIdRef.current === fetchId) {
        setLoadingPrices(false);
      }
    }
  }

  function handleCourseSelect(courseId: string) {
    const course = courses.find((c) => c.id === courseId);
    updateState({
      upsellProductCourseId: courseId,
      upsellProductTitle: course?.title || '',
      upsellPriceIds: {},
      upsellFreeTrialDays: undefined,
    });
    if (courseId) {
      loadUpsellPrices(courseId);
    } else {
      setUpsellPrices([]);
    }
  }

  function updateSection(index: number, updated: CheckoutSection) {
    const newSections = [...activeSections];
    newSections[index] = updated;
    setActiveSections(newSections);
  }

  function removeSection(index: number) {
    setActiveSections(activeSections.filter((_, i) => i !== index));
    setExpandedIndex(undefined);
  }

  function moveSection(index: number, direction: -1 | 1) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= activeSections.length) {
      return;
    }
    const newSections = [...activeSections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setActiveSections(newSections);
    setExpandedIndex(newIndex);
  }

  function addSection(type: CheckoutSection['type']) {
    const acceptDefault = activeTab === 'returners' ? 'Join AI Playground' : 'Yes, add this!';
    const section =
      type === 'cta'
        ? {
            type: 'cta' as const,
            content: { acceptText: acceptDefault, declineText: 'No thanks, continue' },
          }
        : createDefaultSection(type);
    const newSections = [...activeSections, section];
    setActiveSections(newSections);
    setExpandedIndex(newSections.length - 1);
    setShowAddMenu(false);
  }

  function seedReturnersFromDefault() {
    const cloned = structuredClone(state.upsellSections);
    for (const section of cloned) {
      if (section.type === 'cta' && section.content.acceptText === 'Yes, add this!') {
        section.content.acceptText = 'Join AI Playground';
      }
    }
    setActiveSections(cloned);
    setExpandedIndex(undefined);
  }

  const currencies = campaignCurrencies(state);
  const selectedPrices = currencies
    .map((currency) => upsellPrices.find((p) => p.id === state.upsellPriceIds[currency]))
    .filter((p): p is UpsellPrice => !!p);
  const selectedPrice = selectedPrices[0];
  const selectedUpsellCourse = courses.find((c) => c.id === state.upsellProductCourseId);
  const isSubscriptionOrPlan = selectedPrices.some(
    (p) => p.paymentType === 'subscription' || p.paymentType === 'paymentplan',
  );
  const hasFreeTrial = (state.upsellFreeTrialDays ?? 0) > 0;

  return (
    <div>
      <h2 class="text-lg font-semibold text-gray-900 mb-1">Post-Purchase Upsell</h2>
      <p class="text-sm text-gray-500 mb-5">
        Show a branded upsell page after purchase with a one-click accept/decline flow.
      </p>

      <div
        class={`rounded-xl p-4 flex items-center gap-4 cursor-pointer transition-all mb-5 ${
          state.upsellEnabled
            ? 'bg-indigo-50 ring-2 ring-indigo-200'
            : 'border border-gray-200 shadow-sm hover:border-gray-300'
        }`}
        onClick={() => updateState({ upsellEnabled: !state.upsellEnabled })}
      >
        <div
          class={`size-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            state.upsellEnabled ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-400'
          }`}
        >
          <IcoRocketLaunch class="size-4" />
        </div>
        <div class="flex-1 min-w-0">
          <span class="text-sm font-medium text-gray-900 block">Post-purchase upsell</span>
          <span class="text-xs text-gray-500">
            Show buyers a one-click upsell offer after checkout
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={state.upsellEnabled}
          class={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full p-0.5 transition-colors ${
            state.upsellEnabled ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            updateState({ upsellEnabled: !state.upsellEnabled });
          }}
        >
          <span
            class={`pointer-events-none inline-block size-5 rounded-full bg-white shadow-sm transition-transform ${
              state.upsellEnabled ? 'translate-x-6' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {!state.upsellEnabled && (
        <div class="bg-gray-50 rounded-xl p-6 text-center">
          <p class="text-sm text-gray-400">No upsell page will be shown after purchase.</p>
        </div>
      )}

      {state.upsellEnabled && (
        <div class="flex flex-col gap-5">
          <div class="flex flex-col gap-5">
            <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
              <div class="flex items-center gap-2">
                <IcoCreditCard class="size-4 text-gray-400" />
                <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                  Product & Pricing
                </h3>
              </div>
              <div class="flex flex-col gap-1.5 max-w-lg">
                <span class="text-sm font-medium text-gray-700">Upsell product</span>
                <CourseSelect
                  courses={courses}
                  value={state.upsellProductCourseId}
                  onChange={handleCourseSelect}
                  excludeId={state.courseId}
                  placeholder="Search and select a course..."
                />
              </div>

              {state.upsellProductCourseId && (
                <div class="flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-gray-700">Price</span>
                  {currencies.length > 1 && (
                    <p class="text-xs text-gray-500">
                      This campaign sells in {currencies.join(' and ')}. Buyers are offered the
                      upsell in the currency they just paid in, so pick a price for each.
                    </p>
                  )}
                  {loadingPrices ? (
                    <p class="text-sm text-gray-500">Loading prices...</p>
                  ) : upsellPrices.length === 0 ? (
                    <div class="flex flex-col gap-1">
                      <p class="text-sm text-gray-500">
                        No prices found for this course.{' '}
                        <a
                          href={`/manage/courses/${state.upsellProductCourseId}/prices/new`}
                          target="_blank"
                          rel="noreferrer"
                          class="text-indigo-600 hover:underline"
                        >
                          Create one ↗
                        </a>
                        , then{' '}
                        <button
                          type="button"
                          onClick={() => loadUpsellPrices(state.upsellProductCourseId)}
                          class="text-indigo-600 hover:underline"
                        >
                          refresh
                        </button>
                        .
                      </p>
                      {selectedUpsellCourse?.guideName && (
                        <p class="text-xs text-gray-400">
                          Managed by {selectedUpsellCourse.guideName} — if that's not you, ask them
                          to add the price.
                        </p>
                      )}
                    </div>
                  ) : (
                    currencies.map((currency) => {
                      const prices = upsellPrices.filter((p) => (p.currency ?? 'USD') === currency);
                      const chosenId = state.upsellPriceIds[currency];
                      return (
                        <div key={currency} class="flex flex-col gap-2">
                          {currencies.length > 1 && (
                            <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide mt-1">
                              {currency} buyers
                            </span>
                          )}
                          {prices.length === 0 && (
                            <p class="text-sm text-amber-600">
                              This course has no {currency} price, so {currency} buyers won't see
                              this upsell.{' '}
                              <a
                                href={`/manage/courses/${state.upsellProductCourseId}/prices/new`}
                                target="_blank"
                                rel="noreferrer"
                                class="text-indigo-600 hover:underline"
                              >
                                Create one ↗
                              </a>
                              , then{' '}
                              <button
                                type="button"
                                onClick={() => loadUpsellPrices(state.upsellProductCourseId)}
                                class="text-indigo-600 hover:underline"
                              >
                                refresh
                              </button>
                              .
                            </p>
                          )}
                          {prices.map((price) => (
                            <label
                              key={price.id}
                              class={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                chosenId === price.id
                                  ? 'border-indigo-500 bg-indigo-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`upsell-price-${currency}`}
                                value={price.id}
                                checked={chosenId === price.id}
                                onChange={() =>
                                  updateState({
                                    upsellPriceIds: {
                                      ...state.upsellPriceIds,
                                      [currency]: price.id,
                                    },
                                  })
                                }
                                class="text-indigo-600"
                              />
                              <div class="flex flex-col">
                                <span class="text-sm font-medium text-gray-900">
                                  {formatPrice(price)}
                                  {price.name && (
                                    <span class="text-gray-500 font-normal ml-1.5">
                                      ({price.name})
                                    </span>
                                  )}
                                </span>
                                <span class="text-xs text-gray-400">
                                  {price.paymentType === 'subscription'
                                    ? 'Recurring subscription'
                                    : price.paymentType === 'paymentplan'
                                      ? 'Payment plan'
                                      : price.paymentType === 'free'
                                        ? 'No charge'
                                        : 'One-time payment'}
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {selectedPrice && isSubscriptionOrPlan && (
              <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
                <div class="flex items-center gap-2">
                  <IcoGift class="size-4 text-gray-400" />
                  <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    Free Trial
                  </h3>
                </div>
                <label class="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasFreeTrial}
                    onChange={(e) => {
                      const checked = (e.target as HTMLInputElement).checked;
                      updateState({ upsellFreeTrialDays: checked ? 14 : undefined });
                    }}
                    class="rounded border-gray-300 text-indigo-600"
                  />
                  <span class="text-sm text-gray-700">Start with a free trial</span>
                </label>
                {hasFreeTrial && (
                  <div class="flex flex-col gap-2 ml-7">
                    <label class="flex items-center gap-2">
                      <span class="text-sm text-gray-600">Trial length:</span>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={state.upsellFreeTrialDays}
                        onInput={(e) => {
                          const val = parseInt((e.target as HTMLInputElement).value, 10);
                          if (!isNaN(val) && val > 0) {
                            updateState({ upsellFreeTrialDays: val });
                          }
                        }}
                        class="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-20 text-center"
                      />
                      <span class="text-sm text-gray-600">days</span>
                      {(state.upsellFreeTrialDays ?? 0) % 7 === 0 && (
                        <span class="text-xs text-gray-400">
                          ({(state.upsellFreeTrialDays ?? 0) / 7} weeks)
                        </span>
                      )}
                    </label>
                    <p class="text-xs text-gray-500">
                      After the trial, the buyer will be charged{' '}
                      {selectedPrices.map(formatPrice).join(' or ')} automatically.
                    </p>
                  </div>
                )}
              </div>
            )}

            {selectedPrice && (
              <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
                <div class="flex items-center gap-2">
                  <IcoCalendar class="size-4 text-gray-400" />
                  <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    Enrollment
                  </h3>
                </div>
                <div class="flex flex-col gap-2">
                  <label class="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="enrollment-timing"
                      checked={!state.upsellDeferredDate}
                      onChange={() => updateState({ upsellDeferredDate: '' })}
                      class="text-indigo-600"
                    />
                    <span class="text-sm text-gray-700">Immediately after purchase</span>
                  </label>
                  <label class="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="enrollment-timing"
                      checked={!!state.upsellDeferredDate}
                      onChange={() => {
                        if (!state.upsellDeferredDate) {
                          const d = new Date();
                          d.setDate(d.getDate() + 14);
                          updateState({
                            upsellDeferredDate: d.toISOString().slice(0, 10),
                          });
                        }
                      }}
                      class="text-indigo-600"
                    />
                    <span class="text-sm text-gray-700">On a specific date</span>
                  </label>
                </div>
                {state.upsellDeferredDate && (
                  <div class="flex flex-col gap-2 ml-7">
                    <input
                      type="date"
                      value={state.upsellDeferredDate}
                      min={new Date().toISOString().slice(0, 10)}
                      onInput={(e) =>
                        updateState({
                          upsellDeferredDate: (e.target as HTMLInputElement).value,
                        })
                      }
                      class="border border-gray-300 rounded-lg px-3 py-2 text-sm max-w-xs"
                    />
                    <p class="text-xs text-gray-500">
                      The buyer is charged now but gets course access on this date. They'll receive
                      a confirmation email with the enrollment date.
                    </p>
                  </div>
                )}
              </div>
            )}

            {selectedPrice && (
              <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
                <div class="flex items-center gap-2">
                  <IcoTag class="size-4 text-gray-400" />
                  <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    Integrations
                  </h3>
                </div>
                <p class="text-xs text-gray-500">
                  Override the product's default tags for this campaign. Leave empty to use
                  defaults.
                </p>
                <OntraportTagPicker
                  courseId={state.upsellProductCourseId}
                  selectedTags={state.upsellOntraportTags}
                  onChange={(tags) => updateState({ upsellOntraportTags: tags })}
                  placeholder="Search upsell tags..."
                />
              </div>
            )}

            {selectedPrice && <OfferSummary state={state} selectedPrices={selectedPrices} />}
          </div>

          {state.upsellProductCourseId && (
            <div>
              <div class="border-t border-gray-200 pt-5 mt-1 mb-5 flex items-start justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-gray-900 mb-1">Upsell Page Design</h2>
                  <p class="text-sm text-gray-500">
                    Build the page buyers see after checkout with a one-click accept/decline flow.
                  </p>
                </div>
                <BtnSecondary onClick={() => setShowPreview(true)} class="gap-2 flex-shrink-0">
                  <IcoEye class="size-4" />
                  Preview
                </BtnSecondary>
              </div>

              <div class="flex flex-col gap-1 mb-4">
                <div
                  role="tablist"
                  class="inline-flex self-start rounded-lg border border-gray-200 p-1 bg-gray-50"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'default'}
                    onClick={() => {
                      setActiveTab('default');
                      setExpandedIndex(undefined);
                      setShowAddMenu(false);
                      setShowPreview(false);
                    }}
                    class={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'default'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Default page
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'returners'}
                    onClick={() => {
                      setActiveTab('returners');
                      setExpandedIndex(undefined);
                      setShowAddMenu(false);
                      setShowPreview(false);
                    }}
                    class={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                      activeTab === 'returners'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Previous trial users
                    {state.upsellSectionsReturners.length > 0 && (
                      <span class="inline-block size-1.5 rounded-full bg-indigo-500" />
                    )}
                  </button>
                </div>
                <p class="text-xs text-gray-500">
                  {activeTab === 'default'
                    ? 'Shown to first-time buyers and (as a fallback) to anyone whose previous-trial variant is empty.'
                    : 'Shown to buyers who have already used a free AIP trial. They are charged the standard monthly price immediately — no new trial. Leave empty to fall back to the default page with an auto "Welcome back" callout.'}
                </p>
              </div>

              {activeTab === 'returners' && activeSections.length === 0 ? (
                <div class="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center gap-3 text-center">
                  <p class="text-sm text-gray-600 max-w-md">
                    No variant content yet. Previous trial users will see the default page with an
                    auto "Welcome back" callout prepended.
                  </p>
                  <div class="flex items-center gap-2">
                    <BtnSecondary onClick={seedReturnersFromDefault}>
                      Copy from default
                    </BtnSecondary>
                    <BtnSecondary onClick={() => setShowAddMenu(true)}>
                      <IcoPlus class="size-4" />
                      Add Section
                    </BtnSecondary>
                  </div>
                </div>
              ) : null}

              <AIGeneratePanel
                state={state}
                audience={activeTab}
                onGenerated={(sections) => setActiveSections(sections)}
              />

              <div class="flex flex-col gap-2">
                {activeSections.map((section, i) => {
                  const isExpanded = expandedIndex === i;
                  const label = SECTION_TYPE_LABELS[section.type] || section.type;
                  const meta = SECTION_TYPE_META[section.type];
                  const Icon = meta?.icon;

                  return (
                    <div key={i} class="border border-gray-200 rounded-lg overflow-hidden">
                      <div
                        class="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => setExpandedIndex(isExpanded ? undefined : i)}
                      >
                        <span class="text-xs font-semibold text-gray-400 w-6 text-center">
                          {i + 1}
                        </span>
                        {Icon && <Icon class="size-4 text-gray-400 flex-shrink-0" />}
                        <span class="flex-1 text-sm font-medium text-gray-900">{label}</span>
                        <div class="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveSection(i, -1);
                            }}
                            disabled={i === 0}
                            class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            title="Move up"
                          >
                            <IcoChevronUp class="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveSection(i, 1);
                            }}
                            disabled={i === activeSections.length - 1}
                            class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            title="Move down"
                          >
                            <IcoChevronDown class="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSection(i);
                            }}
                            class="p-1 text-gray-400 hover:text-red-500"
                            title="Remove section"
                          >
                            <IcoTrash class="size-4" />
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div class="p-4 border-t border-gray-200">
                          <SectionEditor
                            section={section}
                            onChange={(updated) => updateSection(i, updated)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div class="mt-4 flex items-center gap-3">
                <div class="relative" ref={addMenuRef}>
                  <BtnSecondary onClick={() => setShowAddMenu(!showAddMenu)}>
                    <IcoPlus class="size-4" />
                    Add Section
                  </BtnSecondary>
                  {showAddMenu && (
                    <div class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 w-80 max-h-80 overflow-y-auto">
                      {UPSELL_SECTION_TYPES.map((type) => {
                        const typeMeta = SECTION_TYPE_META[type];
                        const TypeIcon = typeMeta?.icon;
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => addSection(type)}
                            class="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-gray-700 flex items-center gap-3"
                          >
                            {TypeIcon && <TypeIcon class="size-4 text-gray-400 flex-shrink-0" />}
                            <div>
                              <span class="text-sm font-medium block">
                                {SECTION_TYPE_LABELS[type] || type}
                              </span>
                              {typeMeta?.desc && (
                                <span class="text-xs text-gray-400">{typeMeta.desc}</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <BtnSecondary onClick={() => setShowPreview(true)}>Preview Page</BtnSecondary>
              </div>

              {showPreview && (
                <PreviewModal
                  sections={activeSections}
                  brand={state.brand}
                  audience={activeTab}
                  onClose={() => setShowPreview(false)}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
